"use client";

import { ArrowUpRight } from "lucide-react";
import Link from "next/link";
import { ImageWithFallback } from "@/figma/components/figma/ImageWithFallback";
import type { HomePillar } from "@/lib/page-content-types";

const defaultPillars: HomePillar[] = [
  {
    id: "education",
    number: "01",
    title: "Education",
    description:
      "Comprehensive guides to Israeli wine regions, indigenous and international grape varieties, climate influences, and winemaking traditions.",
    image:
      "https://images.unsplash.com/photo-1764438432342-eccb4a489d52?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3aW5lJTIwbWFwJTIwdmluZXlhcmR8ZW58MXx8fHwxNzY4OTk0ODc1fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    imageAlt: "Wine map and vineyard",
    link: "/education",
  },
  {
    id: "directory",
    number: "02",
    title: "Directory",
    description:
      "Structured, searchable database of 250+ wineries, wines, and vintages. Compare community ratings with critic scores and vintage quality.",
    image:
      "https://images.unsplash.com/photo-1765850258622-9d9afa7cf4e2?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3aW5lJTIwY2VsbGFyJTIwYm90dGxlc3xlbnwxfHx8fDE3Njg5OTQ4NzV8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    imageAlt: "Wine cellar bottles",
    link: "/directory",
  },
  {
    id: "tours",
    number: "03",
    title: "Tours",
    description:
      "Plan wine country adventures with detailed visit information, curated tours, seasonal events, and itinerary planning tools.",
    image:
      "https://images.unsplash.com/photo-1641453288558-6dce96ee996c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3aW5lJTIwdGFzdGluZyUyMGdsYXNzfGVufDF8fHx8MTc2ODg5MzA4OHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    imageAlt: "Wine tasting glasses",
    link: "/tours",
  },
  {
    id: "community",
    number: "04",
    title: "Community",
    description:
      "Track your personal cellar, maintain wishlists, share tasting notes, and contribute ratings with fellow wine enthusiasts.",
    image:
      "https://images.unsplash.com/photo-1768306798558-ea16913c6eef?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3aW5lJTIwdmluZXlhcmQlMjBpc3JhZWx8ZW58MXx8fHwxNzY4OTk0ODc0fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    imageAlt: "Wine community",
    link: "/community",
  },
];

export function PillarsSections({ pillars = defaultPillars }: { pillars?: HomePillar[] }) {
  return (
    <div>
      {pillars.map((pillar, index) => (
        <section
          key={pillar.id}
          id={pillar.id}
          className="relative"
          style={{ backgroundColor: index % 2 === 0 ? "var(--paper)" : "var(--linen)" }}
        >
          <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
            <div className="py-32 lg:py-48">
              <div className="grid gap-16 lg:grid-cols-12 lg:gap-24">
                <div className="lg:col-span-5 flex flex-col justify-center">
                  <div className="mb-8">
                    <span
                      style={{
                        fontFamily: "var(--font-display)",
                        fontSize: "var(--text-sm)",
                        fontWeight: 300,
                        color: "var(--slate)",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {pillar.number}
                    </span>
                  </div>

                  <h2
                    className="mb-8"
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: "var(--text-4xl)",
                      fontWeight: 300,
                      lineHeight: "var(--line-height-tight)",
                      color: "var(--charcoal)",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {pillar.title}
                  </h2>

                  <p
                    className="mb-12"
                    style={{
                      fontFamily: "var(--font-body)",
                      fontSize: "var(--text-base)",
                      lineHeight: "var(--line-height-body)",
                      color: "var(--slate)",
                      fontWeight: 300,
                      maxWidth: "480px",
                    }}
                  >
                    {pillar.description}
                  </p>

                  <div>
                    <Link
                      href={pillar.link}
                      className="group inline-flex items-center gap-2 transition-opacity duration-200 hover:opacity-50"
                      style={{
                        fontFamily: "var(--font-body)",
                        fontSize: "var(--text-sm)",
                        color: "var(--charcoal)",
                        fontWeight: 400,
                        letterSpacing: "0.01em",
                      }}
                    >
                      Explore {pillar.title}
                      <ArrowUpRight
                        size={16}
                        className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      />
                    </Link>
                  </div>
                </div>

                <div className="lg:col-span-7">
                  <div className="relative aspect-[4/3] overflow-hidden" style={{ backgroundColor: "var(--stone)" }}>
                    <ImageWithFallback src={pillar.image} alt={pillar.imageAlt} className="h-full w-full object-cover" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {index < pillars.length - 1 && (
            <div
              style={{
                height: "1px",
                backgroundColor: "var(--stone)",
                margin: "0 auto",
                maxWidth: "calc(100% - 4rem)",
              }}
            />
          )}
        </section>
      ))}
    </div>
  );
}
